import { StandardEvents } from '@shopify/events';

const desktop = '(min-width: 990px)';
const emit = (node, name, detail = {}) => node.dispatchEvent(new CustomEvent('tahho:analytics', {
  bubbles: true, detail: { name, ...detail },
}));

class TahhoGallery extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-gallery-track]');
    this.tiles = [...this.querySelectorAll('.tahho-gallery-tile')];
    this.slides = [...this.querySelectorAll('[data-full-slide]')];
    this.dialog = this.querySelector('dialog');
    this.index = 0;
    this.classList.add('is-ready');
    this.addEventListener('click', (event) => {
      const control = event.target.closest('button, [data-gallery-open]');
      if (!control) return;
      if (control.hasAttribute('data-gallery-open')) {
        event.preventDefault();
        this.opener = control;
        this.showFull(Number(control.dataset.galleryOpen));
        this.dialog.showModal();
        this.querySelector('[data-dialog-close]').focus();
        emit(this, 'gallery_fullscreen_open');
      } else if (control.hasAttribute('data-gallery-step')) {
        this.select(this.index + Number(control.dataset.galleryStep));
      } else if (control.hasAttribute('data-gallery-select')) {
        this.select(Number(control.dataset.gallerySelect));
      } else if (control.hasAttribute('data-full-step')) {
        this.showFull(this.fullIndex + Number(control.dataset.fullStep));
      } else if (control.hasAttribute('data-dialog-close')) {
        this.dialog.close();
      } else if (control.hasAttribute('data-zoom')) {
        const zoomed = this.dialog.classList.toggle('is-zoomed');
        control.setAttribute('aria-pressed', String(zoomed));
      }
    }, { signal });
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (matchMedia(desktop).matches) return;
        const rect = this.track.getBoundingClientRect();
        const distances = this.tiles.map(tile => Math.abs(tile.getBoundingClientRect().left - rect.left));
        this.update(distances.indexOf(Math.min(...distances)));
      });
    }, { passive: true, signal });
    this.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (event.target.closest('video, iframe, model-viewer')) return;
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      if (this.dialog.open) this.showFull(this.fullIndex + step);
      else this.select(this.index + step);
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.pauseMedia();
      this.opener?.focus({ preventScroll: true });
    }, { signal });
  }
  disconnectedCallback() { this.controller?.abort(); cancelAnimationFrame(this.frame); }
  pauseMedia() {
    this.querySelectorAll('video').forEach(video => video.pause());
    this.querySelectorAll('iframe').forEach(frame => {
      // Reload only an already loaded external player, stopping playback on close/change.
      if (frame.src) frame.src = frame.src;
    });
  }
  select(index) {
    index = (index + this.tiles.length) % this.tiles.length;
    this.track.scrollTo({ left: this.tiles[index].offsetLeft - this.tiles[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    this.update(index);
  }
  update(index) {
    if (index !== this.index) emit(this, 'gallery_image_view', { index });
    this.index = index;
    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = `${index + 1} / ${this.tiles.length}`;
    this.querySelectorAll('[data-gallery-select]').forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
  }
  showFull(index) {
    this.pauseMedia();
    this.fullIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => { slide.hidden = i !== this.fullIndex; });
    this.dialog.classList.remove('is-zoomed');
    const zoom = this.querySelector('[data-zoom]');
    zoom.setAttribute('aria-pressed', 'false');
    zoom.hidden = !this.slides[this.fullIndex].querySelector('img');
    this.querySelector('[data-dialog-counter]').textContent = `${this.fullIndex + 1} / ${this.slides.length}`;
  }
}

class TahhoProduct extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.classList.add('is-ready');
    this.sticky = this.querySelector('[data-sticky]');
    this.nativeButton = () => this.querySelector('.tahho-purchase button[name="add"]');
    this.section = this.closest('.shopify-section') || this;
    this.pending = false;
    this.generation = 0;
    this.section.addEventListener(StandardEvents.productSelect, event => {
      if (event.target.closest('product-card')) return;
      this.pending = true;
      const generation = ++this.generation;
      this.syncBuy();
      event.promise.then(({ detail }) => {
        if (generation !== this.generation) return;
        const field = this.querySelector('[name="selected_variant"]');
        if (field) field.value = detail?.resource?.id ?? '';
        emit(this, 'variant_select', { variant_id: detail?.resource?.id ?? null });
      }).catch(() => {}).finally(() => {
        if (generation !== this.generation) return;
        this.pending = false;
        this.syncBuy();
      });
    }, { signal });
    this.addEventListener('click', event => {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.hasAttribute('data-sticky-buy')) {
        const button = this.nativeButton();
        if (button && !button.disabled && !this.pending) button.click();
      }
      if (target.dataset.event) emit(this, target.dataset.event);
      if (target.hasAttribute('data-review-expand')) {
        const expanded = target.getAttribute('aria-expanded') !== 'true';
        target.setAttribute('aria-expanded', String(expanded));
        target.previousElementSibling.classList.toggle('is-expanded', expanded);
        target.textContent = expanded ? target.dataset.less : target.dataset.more;
      }
      if (target.closest('.tahho-action') && matchMedia(desktop).matches && (target.name === 'add' || target.dataset.lead)) emit(this, 'desktop_action_panel_cta');
    }, { signal });
    this.querySelectorAll('details[data-event]').forEach(details => details.addEventListener('toggle', () => {
      if (details.open) emit(this, details.dataset.event);
    }, { signal }));
    this.querySelectorAll('[data-scroll-event]').forEach(rail => rail.addEventListener('scroll', () => emit(this, rail.dataset.scrollEvent), { once: true, passive: true, signal }));
    this.querySelectorAll('[data-review-expand]').forEach(button => {
      const copy = button.previousElementSibling;
      button.hidden = copy.scrollHeight <= copy.clientHeight + 1;
    });
    const units = [...this.querySelectorAll('[data-dimension$="-unit"]')];
    try { const saved = sessionStorage.getItem('tahho-unit'); if (['in','ft','cm','m'].includes(saved)) units.forEach(unit => { unit.value = saved; }); } catch {}
    this.addEventListener('input', () => this.validateDimensions(), { signal });
    this.addEventListener('change', event => {
      this.validateDimensions();
      try { if (units.includes(event.target)) sessionStorage.setItem('tahho-unit', event.target.value); } catch {}
    }, { signal });
    this.formObserver = new MutationObserver(() => this.syncBuy());
    const purchase = this.querySelector('.tahho-purchase');
    if (purchase) this.formObserver.observe(purchase, { subtree: true, childList: true, attributes: true, characterData: true });
    this.modalObserver = new MutationObserver(() => this.syncVisibility());
    this.modalObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    document.addEventListener('focusin', () => this.syncVisibility(), { signal });
    document.addEventListener('focusout', () => setTimeout(() => this.syncVisibility(), 0), { signal });
    window.visualViewport?.addEventListener('resize', () => this.syncVisibility(), { signal });
    window.addEventListener('resize', () => this.syncVisibility(), { signal });
    this.syncBuy(); this.syncVisibility(); this.validateDimensions();
    emit(this, 'product_view', { product_id: this.dataset.productId, product_mode: this.dataset.mode });
  }
  disconnectedCallback() { this.controller?.abort(); this.formObserver?.disconnect(); this.modalObserver?.disconnect(); }
  syncBuy() {
    const native = this.nativeButton();
    const sticky = this.querySelector('[data-sticky-buy]');
    if (!sticky || !native) return;
    sticky.disabled = native.disabled || this.pending;
    const text = native.querySelector('.add-to-cart-text')?.textContent?.trim() || native.textContent.trim();
    if (text && sticky.textContent !== text) sticky.textContent = text;
  }
  syncVisibility() {
    if (!this.sticky) return;
    const typing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]), textarea, [contenteditable="true"]');
    const modal = document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]:not([hidden])');
    this.sticky.hidden = Boolean(typing || modal || !this.sticky.children.length);
    const header = document.querySelector('header-component');
    const headerHeight = header?.getBoundingClientRect().height || 0;
    this.style.setProperty('--tahho-sticky-offset', `${headerHeight + 24}px`);
  }
  validateDimensions() {
    const fields = [...this.querySelectorAll('[data-dimension="width"], [data-dimension="height"]')];
    const quote = this.querySelector('[data-quote-button]');
    if (!quote) return;
    const valid = fields.length === 2 && fields.every(field => field.value && field.checkValidity() && Number(field.value) > 0);
    if (valid && quote.disabled) emit(this, 'wall_dimensions_complete');
    quote.disabled = !valid;
  }
}
if (!customElements.get('tahho-gallery')) customElements.define('tahho-gallery', TahhoGallery);
if (!customElements.get('tahho-product')) customElements.define('tahho-product', TahhoProduct);
